/**
 * voiceNavigation — turn a spoken phrase into a `react-router-dom` path.
 *
 * Used by:
 *   - ArcWakeWordListener: when the user says "Hey Arc, open causal
 *     graph" we route them straight to `/brain/causal` instead of
 *     dropping them into Arc to type the same thing.
 *   - ArcConsolePage (final transcript): same behaviour, plus a hint
 *     to the SME like "Try saying 'go to <X>'".
 *
 * Destination list mirrors the 30 entries in `brainPageRegistry.ts` —
 * if you add a new Brain page there, also add a destination here so
 * voice nav covers it. The labels in this file are the registry's
 * `label` field; the triggers are the natural ways an SME might
 * actually *speak* about the page (which can differ from the label —
 * e.g. registry says "Smart Action" but operators usually say
 * "decision optimizer" or "recommendations").
 *
 * Match rules (in `parseNavIntent`):
 *   1. Whole transcript IS a destination trigger          → 0.95
 *   2. Verb + trigger ("go to causal", "open drift")      → 0.90
 *   3. Bare trigger inclusion in ≤6-word utterance        → 0.60
 *
 * The ArcWakeWordListener routes directly only at confidence ≥ 0.85,
 * so rule (3) cases fall through to a normal Arc run instead of
 * hijacking the user's prompt.
 */

interface NavDestination {
  /** Display label (registry-aligned). */
  label: string
  /** Route path. */
  path: string
  /** Lowercase phrases that should match this destination. */
  triggers: string[]
}

const NAV_VERBS = [
  'go to',
  'open',
  'show me',
  'show the',
  'show',
  'take me to',
  'navigate to',
  'jump to',
  'switch to',
  'bring up',
  'pull up',
]

// Destinations are in registry order: OBSERVE → UNDERSTAND → ACT → EVOLVE,
// then Hub-only / cross-cutting destinations at the bottom.
const DESTINATIONS: NavDestination[] = [
  // ───── OBSERVE ─────
  {
    label: 'Live Intelligence',
    path: '/brain/cognitive-twin',
    triggers: [
      'live intelligence',
      'cognitive twin',
      'live brain',
      'brain status',
    ],
  },
  {
    label: 'Model Fitness',
    path: '/brain/model-health',
    triggers: [
      'model health',
      'model fitness',
      'fleet health',
      'model dashboard',
    ],
  },
  {
    label: 'Prediction Accuracy',
    path: '/brain/model-quality',
    triggers: [
      'prediction accuracy',
      'model quality',
      'accuracy dashboard',
      'predictions accuracy',
    ],
  },
  {
    label: 'Evidence Trail',
    path: '/brain/audit',
    triggers: [
      'evidence trail',
      'audit',
      'audit trail',
      'brain audit',
    ],
  },
  {
    label: 'Fairness & Bias',
    path: '/brain/fairness',
    triggers: [
      'fairness',
      'bias',
      'fairness check',
      'bias check',
      'fairness and bias',
    ],
  },
  {
    label: 'Visual Inspection',
    path: '/brain/video-analysis',
    triggers: [
      'visual inspection',
      'video analysis',
      'vision',
      'camera analysis',
    ],
  },
  // ───── UNDERSTAND ─────
  {
    label: 'Relationship Map',
    path: '/brain/causal',
    triggers: [
      'causal',
      'causal graph',
      'relationship map',
      'root cause graph',
      'do calculus',
      'do-calculus',
    ],
  },
  {
    label: 'AI Reasoning',
    path: '/brain/reasoning',
    triggers: [
      'reasoning',
      'ai reasoning',
      'reasoning trace',
      'agent reasoning',
    ],
  },
  {
    label: 'Industry Knowledge',
    path: '/brain/knowledge',
    triggers: [
      'industry knowledge',
      'knowledge browser',
      'knowledge base',
      'documents',
      'kb',
    ],
  },
  {
    label: 'Entity Knowledge Graph',
    path: '/brain/knowledge-graph',
    triggers: [
      'knowledge graph',
      'entity graph',
      'entity knowledge graph',
      'kg',
    ],
  },
  {
    label: 'Rule + AI Engine',
    path: '/brain/neuro-symbolic',
    triggers: [
      'neuro symbolic',
      'neuro-symbolic',
      'rule engine',
      'rule and ai engine',
      'rule plus ai',
    ],
  },
  // ───── ACT ─────
  {
    label: 'Smart Action',
    path: '/brain/decision-optimizer',
    triggers: [
      'smart action',
      'decision optimizer',
      'recommendation',
      'recommendations',
      'next best action',
    ],
  },
  {
    label: 'What-If Scenarios',
    path: '/brain/hypothesis-lab',
    triggers: [
      'hypothesis lab',
      'hypothesis',
      'what if',
      'what-if',
      'what-if scenarios',
      'debate',
      'cross examine',
      'cross-examine',
    ],
  },
  {
    label: 'AI Experiments',
    path: '/brain/experiment-lab',
    triggers: [
      'experiment lab',
      'ai experiments',
      'experiments',
      'experimentation',
      'a/b test',
      'rollout',
    ],
  },
  {
    label: 'Behavior Simulator',
    path: '/brain/twin',
    triggers: [
      'behavior simulator',
      'twin',
      'digital twin',
      'simulator',
    ],
  },
  {
    label: 'Physics Validator',
    path: '/brain/physics',
    triggers: [
      'physics',
      'physics lab',
      'physics validator',
      'physics validation',
    ],
  },
  {
    label: 'Design Optimizer',
    path: '/brain/design-studio',
    triggers: [
      'design studio',
      'design optimizer',
      'generative design',
    ],
  },
  {
    label: 'Ask The AI',
    path: '/brain/chat',
    triggers: [
      'ask the ai',
      'ask ai',
      'brain chat',
      'ai chat',
    ],
  },
  // ───── EVOLVE ─────
  {
    label: 'Auto-Improvement',
    path: '/brain/self-evolution',
    triggers: [
      'auto improvement',
      'self evolution',
      'self-evolution',
      'evolve',
      'evolution',
    ],
  },
  {
    label: 'Auto-Recovery',
    path: '/brain/healing',
    triggers: [
      'healing',
      'auto recovery',
      'auto-recovery',
      'self healing',
      'self-healing',
    ],
  },
  {
    label: 'Learning Memory',
    path: '/brain/memory',
    triggers: [
      'memory',
      'learning memory',
      'episodic memory',
      'memory map',
    ],
  },
  {
    label: 'Scenario Generator',
    path: '/brain/dreaming',
    triggers: [
      'dreaming',
      'scenario generator',
      'replay',
      'consolidation',
    ],
  },
  {
    label: 'Robustness Shield',
    path: '/brain/security',
    triggers: [
      'security',
      'brain security',
      'robustness',
      'robustness shield',
      'red team',
      'red-team',
      'adversarial',
    ],
  },
  {
    label: 'Confidence Gate',
    path: '/brain/metacognition',
    triggers: [
      'metacognition',
      'meta cognition',
      'confidence gate',
      'self reflection',
      'self-reflection',
    ],
  },
  {
    label: 'Objective Alignment',
    path: '/brain/goals',
    triggers: [
      'goals',
      'okrs',
      'objectives',
      'objective alignment',
    ],
  },
  {
    label: 'Multi-Site Learning',
    path: '/brain/federated-learning',
    triggers: [
      'federated learning',
      'federated',
      'multi site learning',
      'multi-site learning',
      'cross-site learning',
    ],
  },
  {
    label: 'Autonomous Agents',
    path: '/brain/agent-teams',
    triggers: [
      'autonomous agents',
      'agent teams',
      'agents page',
    ],
  },
  {
    label: 'Weekly AI Upgrade',
    path: '/brain/meta-orchestra',
    triggers: [
      'meta orchestra',
      'meta-orchestra',
      'weekly ai upgrade',
      'weekly upgrade',
    ],
  },
  {
    label: 'Team Workspace',
    path: '/brain/artifacts',
    triggers: [
      'artifacts',
      'team workspace',
      'workspace',
    ],
  },
  {
    label: 'New Industry Detector',
    path: '/brain/industry-discovery',
    triggers: [
      'industry discovery',
      'new industry',
      'industry detector',
      'new industry detector',
    ],
  },
  // ───── Cross-cutting / non-Brain ─────
  {
    label: 'Ryekronix Console',
    path: '/arc',
    triggers: [
      'arc',
      'arc console',
      'agent console',
      'oracle console',
    ],
  },
  {
    label: 'AI Assistant',
    path: '/playground/agi',
    triggers: [
      'ai assistant',
      'agi',
      'playground',
    ],
  },
  {
    label: 'Capabilities',
    path: '/about/capabilities',
    triggers: [
      'capabilities',
      'why ryedore',
      'differentiators',
    ],
  },
  {
    label: 'Command Center',
    path: '/dashboard',
    triggers: [
      'dashboard',
      'command center',
      'home',
    ],
  },
]

export interface NavIntent {
  label: string
  path: string
  /** Confidence 0-1 (higher = more certain). */
  confidence: number
  /** The matched trigger text — useful for telling the user what we heard. */
  matched: string
}

/**
 * Parse a transcript for a navigation intent. Returns null if no
 * destination matched with reasonable confidence.
 */
export function parseNavIntent(text: string): NavIntent | null {
  const t = text.trim().toLowerCase().replace(/[?.!]+$/g, '')
  if (!t) return null

  let best: NavIntent | null = null

  for (const dest of DESTINATIONS) {
    for (const trig of dest.triggers) {
      const tl = trig.toLowerCase()

      // Rule 1: exact match (the entire transcript is the trigger).
      if (t === tl) {
        return { label: dest.label, path: dest.path, confidence: 0.95, matched: trig }
      }

      // Rule 2: verb + trigger ("go to causal", "show me drift").
      for (const v of NAV_VERBS) {
        if (t.includes(`${v} ${tl}`) || t.includes(`${v} the ${tl}`)) {
          const cand: NavIntent = {
            label: dest.label,
            path: dest.path,
            confidence: 0.9,
            matched: `${v} ${trig}`,
          }
          if (!best || cand.confidence > best.confidence) best = cand
        }
      }

      // Rule 3: bare inclusion in ≤6-word utterance — confidence 0.6.
      // Only used as a fallback hint; ArcWakeWordListener routes
      // directly only at ≥0.85, so this rule won't hijack the prompt.
      if (t.includes(tl) && t.split(/\s+/).length <= 6) {
        const cand: NavIntent = {
          label: dest.label,
          path: dest.path,
          confidence: 0.6,
          matched: trig,
        }
        if (!best || cand.confidence > best.confidence) best = cand
      }
    }
  }

  return best
}

/**
 * Suggestion list — for surfacing "you can also say…" hints in UI.
 */
export const NAV_EXAMPLES: string[] = [
  '"Hey Ryekronix, show me drift"',
  '"Hey Ryekronix, go to causal graph"',
  '"Hey Ryekronix, open hypothesis lab"',
  '"Hey Ryekronix, switch to model health"',
]

/**
 * Read-only export for the audit script — lets the bundle-free Node
 * harness exercise the same matcher rules without re-listing
 * destinations.
 */
export const __NAV_DESTINATIONS_FOR_AUDIT = DESTINATIONS
